import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, CheckCircle2, XCircle, Clock } from "lucide-react";

export default function PaymentSuccess() {
    const navigate = useNavigate();
    const [status, setStatus] = useState("checking");
    const [details, setDetails] = useState(null);

    const check = useCallback(async (sessionId, attempts = 0) => {
        if (attempts >= 8) {
            setStatus("timeout");
            return;
        }
        try {
            const { data } = await api.get(`/payments/stripe/status/${sessionId}`);
            setDetails(data);
            if (data.payment_status === "paid") {
                setStatus("success");
                return;
            }
            if (data.status === "expired") {
                setStatus("expired");
                return;
            }
            setTimeout(() => check(sessionId, attempts + 1), 2000);
        } catch (e) {
            setStatus("error");
        }
    }, []);

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const sid = params.get("session_id");
        if (!sid) { setStatus("no-session"); return; }
        check(sid);
    }, [check]);

    return (
        <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
            <Card className="w-full max-w-md p-8 border border-slate-200 rounded-md shadow-none text-center">
                {status === "checking" && (
                    <>
                        <Loader2 className="w-12 h-12 mx-auto text-[#0A1C3E] animate-spin" />
                        <div className="mt-4 font-display font-semibold text-xl">Confirming payment...</div>
                        <div className="text-sm text-slate-500 mt-1">Please wait while we verify with Stripe.</div>
                    </>
                )}
                {status === "success" && (
                    <>
                        <CheckCircle2 className="w-12 h-12 mx-auto text-emerald-600" />
                        <div className="mt-4 font-display font-semibold text-xl text-slate-900">Payment Successful</div>
                        {details?.amount && <div className="text-2xl font-mono font-bold mt-2">₹{Number(details.amount).toLocaleString()}</div>}
                        <div className="text-sm text-slate-500 mt-1">Applied to reservation folio.</div>
                        <Button onClick={() => navigate("/payments")} className="mt-6 bg-[#0A1C3E] rounded-sm">Back to Payments</Button>
                    </>
                )}
                {(status === "expired" || status === "timeout" || status === "error") && (
                    <>
                        <XCircle className="w-12 h-12 mx-auto text-red-500" />
                        <div className="mt-4 font-display font-semibold text-xl">Payment {status}</div>
                        <Button onClick={() => navigate("/payments")} variant="outline" className="mt-6">Try again</Button>
                    </>
                )}
                {status === "no-session" && (
                    <>
                        <Clock className="w-12 h-12 mx-auto text-slate-400" />
                        <div className="mt-4 font-display font-semibold text-xl">No session id provided</div>
                        <Button onClick={() => navigate("/")} className="mt-6">Home</Button>
                    </>
                )}
            </Card>
        </div>
    );
}
